/**
 * WebMCP — expose key site actions to AI agents via navigator.modelContext (Chrome EPP).
 * @see https://webmachinelearning.github.io/webmcp/
 */

export interface WebMcpToolDefinition {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (input: Record<string, unknown>, signal?: AbortSignal) => Promise<unknown>;
}

interface ModelContextApi {
  registerTool: (tool: WebMcpToolDefinition) => void;
  unregisterTool?: (name: string) => void;
}

interface NavigatorWithModelContext extends Navigator {
  modelContext?: ModelContextApi;
}

const SITE_ORIGIN = "https://tellerwand.com";

export const WEBMCP_TOOLS: WebMcpToolDefinition[] = [
  {
    name: "navigate",
    description: "Navigate the browser to a TellerWand page path (e.g. /pricing, /faq, /auth).",
    inputSchema: {
      type: "object",
      properties: {
        path: {
          type: "string",
          description: "Site-relative path starting with /",
        },
      },
      required: ["path"],
    },
    execute: (input) => {
      const pathValue = input.path;
      if (typeof pathValue !== "string") {
        return Promise.reject(new Error("path must be a string"));
      }
      if (!pathValue.startsWith("/") || pathValue.startsWith("//")) {
        return Promise.reject(new Error("path must be a same-origin relative path"));
      }
      globalThis.location.assign(pathValue);
      return Promise.resolve({ ok: true, path: pathValue });
    },
  },
  {
    name: "get_site_overview",
    description: "Fetch the llms.txt site overview for agents (markdown).",
    inputSchema: {
      type: "object",
      properties: {},
    },
    execute: async (_input, signal) => {
      const response = await fetch("/llms.txt", {
        headers: { Accept: "text/plain" },
        signal,
      });
      if (!response.ok) {
        throw new Error(`Failed to load llms.txt: ${response.status}`);
      }
      const text = await response.text();
      return { content: text };
    },
  },
  {
    name: "discover_api",
    description: "Fetch the RFC 9727 API catalog linkset for automated API discovery.",
    inputSchema: {
      type: "object",
      properties: {},
    },
    execute: async (_input, signal) => {
      const response = await fetch("/.well-known/api-catalog", {
        headers: { Accept: "application/linkset+json" },
        signal,
      });
      if (!response.ok) {
        throw new Error(`Failed to load API catalog: ${response.status}`);
      }
      return response.json();
    },
  },
  {
    name: "search_faq",
    description: "Open the FAQ page; optional query is passed as a URL hash for in-page search.",
    inputSchema: {
      type: "object",
      properties: {
        query: {
          type: "string",
          description: "Optional FAQ search hint",
        },
      },
    },
    execute: (input) => {
      const queryValue = input.query;
      const query = typeof queryValue === "string" ? queryValue : "";
      const hash = query ? `#q=${encodeURIComponent(query)}` : "";
      globalThis.location.assign(`/faq${hash}`);
      return Promise.resolve({ ok: true, url: `${SITE_ORIGIN}/faq${hash}` });
    },
  },
];

let registeredToolNames: string[] = [];
let abortController: AbortController | null = null;

/**
 * Register Web Mcp Tools.
 */
export function registerWebMcpTools(): () => void {
  const navigatorWithMcp = globalThis.navigator as NavigatorWithModelContext;
  const { modelContext } = navigatorWithMcp;
  if (!modelContext?.registerTool) {
    return () => undefined;
  }

  abortController?.abort();
  abortController = new AbortController();
  const { signal } = abortController;

  registeredToolNames.forEach((name) => {
    modelContext.unregisterTool?.(name);
  });
  registeredToolNames = [];

  for (const tool of WEBMCP_TOOLS) {
    modelContext.registerTool({
      ...tool,
      execute: (input, toolSignal) => {
        const combinedSignal = toolSignal ?? signal;
        return tool.execute(input, combinedSignal);
      },
    });
    registeredToolNames.push(tool.name);
  }

  return () => {
    for (const name of registeredToolNames) {
      modelContext.unregisterTool?.(name);
    }
    registeredToolNames = [];
    abortController?.abort();
    abortController = null;
  };
}

export function initWebMcp(): void {
  if (globalThis.window === undefined) {
    return;
  }
  const unregister = registerWebMcpTools();
  globalThis.window.addEventListener("pagehide", unregister, { once: true });
}
